import React, { useRef, useState, useEffect } from "react"

import styles from "./NotesContainer.module.css"

import { useEffectSkipFirstRender } from "~/hooks/useEffectSkipFirstRender"

import { Note, Permissions } from "../types"

import NoteInput from "./NoteInput"
import NoteWrapper from "./NoteWrapper"

type Props = {
  notes: ReadonlyArray<Note>
  permissions: Permissions
  onUpdate: (noteId: number, note: string) => Promise<void>
  onDelete: (noteId: number) => Promise<void>
  onCreate: (note: string) => Promise<void>
  focusedNoteId?: number
}

const NotesContainer = (props: Props) => {
  const { notes, permissions, focusedNoteId } = props
  const [highlightedNoteId, setHighlightedNoteId] = useState<number | null>(
    null,
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const noteRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const hasScrolled = useRef(false)
  const scrollAttempts = useRef(0)

  useEffectSkipFirstRender(() => {
    if (focusedNoteId && hasScrolled.current === false) return

    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: "smooth",
    })
  }, [notes?.length])

  const scrollToNote = (noteId: number) => {
    const noteElement = noteRefs.current.get(noteId)
    if (!noteElement || !containerRef.current) return

    const containerRect = containerRef.current.getBoundingClientRect()
    const noteRect = noteElement.getBoundingClientRect()

    // Check if note is not in viewport
    if (
      noteRect.top < containerRect.top ||
      noteRect.bottom > containerRect.bottom
    ) {
      containerRef.current.scrollTo({
        top: noteElement.offsetTop - containerRef.current.offsetTop - 20,
        behavior: "smooth",
      })

      // Wait for scroll to complete before highlighting
      const checkScrollComplete = () => {
        const newContainerRect = containerRef.current?.getBoundingClientRect()
        const newNoteRect = noteElement.getBoundingClientRect()

        if (
          newContainerRect &&
          newNoteRect.top >= newContainerRect.top &&
          newNoteRect.bottom <= newContainerRect.bottom
        ) {
          setHighlightedNoteId(noteId)
          setTimeout(() => setHighlightedNoteId(null), 2000)
        }
      }

      requestAnimationFrame(checkScrollComplete)
    } else {
      // If note is already in viewport, highlight immediately
      setHighlightedNoteId(noteId)
      setTimeout(() => setHighlightedNoteId(null), 2000)
    }
  }

  useEffect(() => {
    if (!focusedNoteId || hasScrolled.current) return

    const attemptScroll = () => {
      const noteElement = noteRefs.current.get(focusedNoteId)
      if (noteElement) {
        scrollToNote(focusedNoteId)
        hasScrolled.current = true
        scrollAttempts.current = 0
      } else if (scrollAttempts.current < 10) {
        scrollAttempts.current += 1
        requestAnimationFrame(attemptScroll)
      }
    }

    attemptScroll()
  }, [focusedNoteId, notes])

  const canCreateNote = permissions.canCreate()

  return (
    <React.Suspense>
      <section>
        <div
          data-test="notes-container"
          ref={containerRef}
          className={styles.container}
        >
          {notes.map((note) => (
            <div
              key={note.id}
              ref={(el) => {
                if (el) noteRefs.current.set(note.id, el)
                else noteRefs.current.delete(note.id)
              }}
            >
              <NoteWrapper
                note={note}
                permissions={permissions}
                onUpdate={props.onUpdate}
                onDelete={props.onDelete}
                isHighlighted={highlightedNoteId === note.id}
              />
            </div>
          ))}
        </div>
        {canCreateNote && (
          <div className={styles.inputContainer}>
            <NoteInput
              onSubmit={props.onCreate}
              forceSuggestionsAboveCursor={true}
              permissions={permissions}
            />
          </div>
        )}
      </section>
    </React.Suspense>
  )
}

export default NotesContainer
